<template>
    <Loading v-if="!didLoadedAll"></Loading>
    <div v-else class="mt-5">
      <crafting-question-dialog
          :show-dialog="questionDialog.show"
          :question-id="questionDialog.questionId"
          :subject-id="subjectId"
          @close="closeCraftingQuestionsDialog"
      />
      <div
          class="w-100 d-flex flex-column justify-center align-center"
          v-if="getAllQuestions.length === 0">
        <img
            style="width: 400px"
            :src="noQuestionImage"
            alt="noQuestionImage">
        <div class="text-h5">{{$t('gamification.no_questions')}}</div>
        <div class="text-h6 font-weight-light text-grey-darken-1">{{$t('gamification.no_questions_added')}}</div>
        <PrimaryButton class="mt-5" @click="openQuestionDialog(null)">
          {{$t('gamification.add_question')}}
        </PrimaryButton>
      </div>
      <div v-else>
        <v-data-table
            v-model="selectedItems"
            :search="search"
            :headers="headers"
            :items="filteredQuestions"
            :loading-text="$t('gamification.loading')"
            show-select
            return-object
            items-per-page="25"
        >
          <template v-slot:top>
            <v-row>
              <v-col cols="6">
                <v-text-field
                    color="light-green darken-1"
                    v-model="search"
                    prepend-icon="search"
                    :label="$t('gamification.search_question')"
                    clearable
                    hide-details
                    variant="underlined"
                />
              </v-col>
              <v-spacer />
              <v-col cols="4">
                <v-select
                    v-model="selectedType"
                    :items="questionTypes"
                    item-title="title"
                    item-value="value"
                    return-object
                    clearable
                    color="primary"
                    :placeholder="$t('gamification.question_type')"
                    variant="underlined"
                />
              </v-col>
            </v-row>
          </template>
          <template v-slot:no-data>
            {{ $t('gamification.no_data') }}
          </template>

          <template v-slot:item.text="{item}">
            {{ item.text }}
          </template>

          <template v-slot:item.date="{item}">
            <span>{{ formatDate(item.date) }}</span>
          </template>

          <template v-slot:item.owner="{item}">
            <span>{{ item.owner?.fullName }}</span>
          </template>

          <template v-slot:item.actions="{item}">
            <div class="d-flex ga-1">
              <v-btn @click="openQuestionDialog(item.id)" color="grey-darken-3" variant="text" size="small" icon="edit" />
              <v-btn @click="attemptToDelete(item.id)" color="red-darken-2" variant="text" size="small" icon="delete" />
            </div>
          </template>

          <template v-slot:bottom>
            <div class="d-flex justify-lg-space-between align-center">
              <div v-if="selectedItems.length!==0">
                <v-btn small class="actionsDetails ml-5"
                       @click.stop="dialog = true"
                       :disabled="selectedItems.length === 0"
                > {{ $t('gamification.delete') }}
                </v-btn>
              </div>
              <v-spacer />
              <v-data-table-footer />
            </div>
          </template>
        </v-data-table>
        <DeleteDialog
            :method="deleteQuestions"
            :dialog="dialog"
            :loading="loading"
            v-on:childToParent="closeDialog">
        </DeleteDialog>
      </div>
    </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import moment from "moment";
import DeleteDialog from "@/components/map/DeleteDialog";
import Loading from "@/components/Loading";
import PrimaryButton from "@/components/buttons/PrimaryButton.vue";
import NoSubjects from "@/assets/gamification/no_subjects.png";
import CraftingQuestionDialog from "@/components/gamification/question/CraftingQuestionDialog.vue";

export default {
    name: "Questions",

    components: {CraftingQuestionDialog, PrimaryButton, DeleteDialog, Loading},

    mounted() {
        this.fetchQuestionsBySubjectId(this.subjectId);
    },

    data() {
      return {
        selectedItems: [],
        selectedType: null,
        dialog: false,
        headers: [
            { title: this.$t('gamification.question'), align: 'left', sortable: true, value: 'text', width: '50%'},
            { title: this.$t('gamification.created_by'), align: 'left', sortable: true, value: 'owner', width: '20%'},
            { title: this.$t('gamification.last_changes'), align: 'left', sortable: true, value: 'date', width: '20%'},
            { title: this.$t('gamification.action'), align: 'left', sortable: false, value: 'actions', width: '10%'},
        ],
        toDelete: null,
        loading: false,
        noQuestionImage: NoSubjects,
        questionDialog: {
          show: false,
          questionId: null
        },
        search: ''
      }
    },

    computed: {
      ...mapGetters('Questions', ['getQuestionsBySubjectId', 'didLoadedQuestions','getQuestionTypes','fetchedQuestionsBySubjectId']),

      getAllQuestions() {
        return this.fetchedQuestionsBySubjectId
      },

      didLoadedAll() {
        return this.didLoadedQuestions;
      },

      subjectId(){
        return Number(this.$route.params.subjectId);
      },

      filteredQuestions(){
        if(this.selectedType){
          return this.getAllQuestions.filter(q => q.type === this.selectedType.value)
        } else {
          return this.getAllQuestions
        }
      },
      questionTypes(){
        return Object.values(this.getQuestionTypes).map(qt => {
          return {
            ...qt,
            title: this.$t(qt.locKey)
          }
        })
      }
    },

    methods: {
      ...mapActions('Questions', ['fetchQuestionsBySubjectId', 'deleteQuestionById']),
      openQuestionDialog(id){
        this.questionDialog.questionId = id
        this.questionDialog.show = true
      },
      formatDate(date) {
          return moment(date).format('lll')
      },
      async deleteQuestions() {
        this.loading = true
        for (const question of this.selectedItems){
          await this.deleteQuestionById(question.id)
        }
        if(this.toDelete){
          await this.deleteQuestionById(this.toDelete)
        }
        this.selectedItems = [];
        this.toDelete = null
        this.dialog = false;
        this.loading = false
      },
      closeDialog() {
          this.dialog = false;
          this.toDelete = null
      },
      async closeCraftingQuestionsDialog(){
        this.questionDialog.show = false
        await this.fetchQuestionsBySubjectId(this.subjectId)
      },
      attemptToDelete(id){
        this.toDelete = id
        this.dialog = true
      },
    }

}
</script>

<style scoped>

.actionsDetails {
    background: rgba(0, 0, 0, 0.45) !important;
    color: white !important;
}

</style>